import { formatDate, formatIDR } from "../lib/format";

// Strip non-digits, handle leading 0 as Indonesian (62)
const normalizePhone = (raw) => {
  if (!raw) return "";
  let p = String(raw).replace(/\D/g, "");
  if (p.startsWith("0")) p = "62" + p.slice(1);
  if (p.startsWith("8")) p = "62" + p;
  return p;
};

const HOMESTAY = "Meos Ambower Homestay";

// EN date formatter (e.g. "20 Feb 2026")
const formatDateEN = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// Default language: explicit on booking, else infer from platform, else 'id'
export const inferLanguage = (booking) => {
  if (booking?.language === "en" || booking?.language === "id") return booking.language;
  if (booking?.platform === "Booking.com" || booking?.platform === "Airbnb") return "en";
  return "id";
};

// 1. Confirmation
const buildConfirmID = (b, h) => {
  const lines = [
    `Halo ${b.guest_name},`,
    "",
    `Terima kasih sudah booking di *${h}* 🌴`,
    "",
    `Berikut detail reservasi Anda:`,
    `• Kamar: *${b.room_name}*`,
    `• Check-in: *${formatDate(b.check_in)}*`,
    `• Check-out: *${formatDate(b.check_out)}* (${b.nights} malam)`,
    `• Jumlah tamu: ${b.guests} orang`,
    `• Total: *${formatIDR(b.total_price)}*`,
  ];
  const outstanding = (b.total_price || 0) - (b.deposit || 0);
  if ((b.deposit || 0) > 0) {
    lines.push(`• DP diterima: ${formatIDR(b.deposit)}`);
    if (outstanding > 0) lines.push(`• Sisa pembayaran: *${formatIDR(outstanding)}*`);
  }
  lines.push(
    "",
    `Status booking: *${b.status}*`,
    "",
    "Mohon konfirmasi balasan ini untuk validasi booking. Sampai jumpa di Raja Ampat! 🏝️",
  );
  return lines.join("\n");
};

const buildConfirmEN = (b, h) => {
  const lines = [
    `Hello ${b.guest_name},`,
    "",
    `Thank you for booking with *${h}* 🌴`,
    "",
    `Here are your reservation details:`,
    `• Room: *${b.room_name}*`,
    `• Check-in: *${formatDateEN(b.check_in)}*`,
    `• Check-out: *${formatDateEN(b.check_out)}* (${b.nights} night${b.nights > 1 ? "s" : ""})`,
    `• Guests: ${b.guests} ${b.guests > 1 ? "people" : "person"}`,
    `• Total: *${formatIDR(b.total_price)}*`,
  ];
  const outstanding = (b.total_price || 0) - (b.deposit || 0);
  if ((b.deposit || 0) > 0) {
    lines.push(`• Deposit received: ${formatIDR(b.deposit)}`);
    if (outstanding > 0) lines.push(`• Balance due: *${formatIDR(outstanding)}*`);
  }
  lines.push(
    "",
    `Booking status: *${b.status}*`,
    "",
    "Please reply to confirm your reservation. See you in Raja Ampat! 🏝️",
  );
  return lines.join("\n");
};

// 2. Reminder H-1
const buildReminderID = (b, h) => [
  `Halo ${b.guest_name}! 👋`,
  "",
  `Sekedar mengingatkan, BESOK *${formatDate(b.check_in)}* Anda check-in di *${h}* 🌴`,
  "",
  `Detail reservasi:`,
  `• Kamar: *${b.room_name}*`,
  `• Tamu: ${b.guests} orang`,
  `• ${b.nights} malam, hingga ${formatDate(b.check_out)}`,
  "",
  `Tips perjalanan:`,
  `• Bawa baju renang & sunblock 🌞`,
  `• Cash secukupnya (ATM jauh dari lokasi)`,
  `• Konfirmasi jam kedatangan agar kami siapkan penjemputan`,
  "",
  `Sampai jumpa besok! 🏝️`,
].join("\n");

const buildReminderEN = (b, h) => [
  `Hello ${b.guest_name}! 👋`,
  "",
  `Just a friendly reminder — TOMORROW *${formatDateEN(b.check_in)}* you'll be checking in at *${h}* 🌴`,
  "",
  `Reservation details:`,
  `• Room: *${b.room_name}*`,
  `• Guests: ${b.guests} ${b.guests > 1 ? "people" : "person"}`,
  `• ${b.nights} night${b.nights > 1 ? "s" : ""}, until ${formatDateEN(b.check_out)}`,
  "",
  `Travel tips:`,
  `• Bring swimwear & sunblock 🌞`,
  `• Cash on hand (ATMs are far from the property)`,
  `• Confirm your arrival time so we can arrange pickup`,
  "",
  `See you tomorrow! 🏝️`,
].join("\n");

// 3. Payment reminder
const buildPaymentID = (b, h) => {
  const outstanding = (b.total_price || 0) - (b.deposit || 0);
  const lines = [
    `Halo ${b.guest_name},`,
    "",
    `Berikut info pembayaran reservasi Anda di *${h}*:`,
    "",
    `• Kamar: *${b.room_name}*`,
    `• Check-in: ${formatDate(b.check_in)}`,
    `• Total tagihan: *${formatIDR(b.total_price)}*`,
  ];
  if ((b.deposit || 0) > 0) lines.push(`• DP terbayar: ${formatIDR(b.deposit)} ✅`);
  if (outstanding > 0) lines.push(`• *Sisa pembayaran: ${formatIDR(outstanding)}*`);
  else lines.push(`• Status: *LUNAS* ✅`);
  lines.push(
    "",
    outstanding > 0
      ? `Mohon transfer pelunasan sebelum check-in agar proses lebih lancar. Terima kasih! 🙏`
      : `Pembayaran sudah lunas. Terima kasih atas kepercayaannya! 🙏`,
  );
  return lines.join("\n");
};

const buildPaymentEN = (b, h) => {
  const outstanding = (b.total_price || 0) - (b.deposit || 0);
  const lines = [
    `Hello ${b.guest_name},`,
    "",
    `Here is the payment info for your reservation at *${h}*:`,
    "",
    `• Room: *${b.room_name}*`,
    `• Check-in: ${formatDateEN(b.check_in)}`,
    `• Total: *${formatIDR(b.total_price)}*`,
  ];
  if ((b.deposit || 0) > 0) lines.push(`• Deposit paid: ${formatIDR(b.deposit)} ✅`);
  if (outstanding > 0) lines.push(`• *Balance due: ${formatIDR(outstanding)}*`);
  else lines.push(`• Status: *PAID IN FULL* ✅`);
  lines.push(
    "",
    outstanding > 0
      ? `Please transfer the balance before check-in to streamline arrival. Thank you! 🙏`
      : `Payment received in full. Thank you for your trust! 🙏`,
  );
  return lines.join("\n");
};

// 4. Thank you + review
const buildThankYouID = (b, h) => [
  `Halo ${b.guest_name}! 🌴`,
  "",
  `Terima kasih sudah menginap di *${h}* selama ${b.nights} malam.`,
  "",
  `Semoga liburan di Raja Ampat berkesan dan kami senang bisa menjadi bagian dari pengalaman Anda. 🏝️✨`,
  "",
  `Kalau berkenan, mohon bantu kami dengan:`,
  `• Memberikan rating/review di platform booking`,
  `• Foto/video bisa di-tag *@meos_ambower* di Instagram`,
  `• Rekomendasikan ke teman & keluarga 🙏`,
  "",
  `Sampai jumpa di kunjungan berikutnya!`,
].join("\n");

const buildThankYouEN = (b, h) => [
  `Hello ${b.guest_name}! 🌴`,
  "",
  `Thank you for staying with *${h}* for ${b.nights} night${b.nights > 1 ? "s" : ""}.`,
  "",
  `We hope your time in Raja Ampat was memorable and we're happy to have been part of your journey. 🏝️✨`,
  "",
  `If you enjoyed your stay, please consider:`,
  `• Leaving a review/rating on your booking platform`,
  `• Tagging *@meos_ambower* on Instagram with your photos`,
  `• Recommending us to friends & family 🙏`,
  "",
  `See you on your next visit!`,
].join("\n");

// Public builders — pick lang automatically
const builders = {
  confirm: { id: buildConfirmID, en: buildConfirmEN },
  reminder: { id: buildReminderID, en: buildReminderEN },
  payment: { id: buildPaymentID, en: buildPaymentEN },
  thankyou: { id: buildThankYouID, en: buildThankYouEN },
};

const buildMsg = (key, booking, langOverride, homestayName = HOMESTAY) => {
  const lang = langOverride || inferLanguage(booking);
  const fn = builders[key]?.[lang] || builders[key].id;
  return fn(booking, homestayName);
};

export const buildWhatsAppMessage = (b, h, lang) => buildMsg("confirm", b, lang, h);
export const buildReminderMessage = (b, h, lang) => buildMsg("reminder", b, lang, h);
export const buildPaymentReminderMessage = (b, h, lang) => buildMsg("payment", b, lang, h);
export const buildThankYouMessage = (b, h, lang) => buildMsg("thankyou", b, lang, h);

// Build wa.me URL
const buildUrl = (booking, message) => {
  const phone = normalizePhone(booking.phone_number);
  const msg = encodeURIComponent(message);
  return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
};

export const whatsappUrl = (booking, lang) => buildUrl(booking, buildMsg("confirm", booking, lang));
export const whatsappReminderUrl = (booking, lang) => buildUrl(booking, buildMsg("reminder", booking, lang));
export const whatsappPaymentUrl = (booking, lang) => buildUrl(booking, buildMsg("payment", booking, lang));
export const whatsappThankYouUrl = (booking, lang) => buildUrl(booking, buildMsg("thankyou", booking, lang));

// Template menu items (used by dropdown — bilingual labels too)
export const WA_TEMPLATES = [
  {
    key: "confirm",
    label: { id: "Konfirmasi Booking", en: "Booking Confirmation" },
    url: whatsappUrl,
    icon: "✅",
  },
  {
    key: "reminder",
    label: { id: "Reminder H-1 Check-in", en: "Check-in Reminder (D-1)" },
    url: whatsappReminderUrl,
    icon: "⏰",
  },
  {
    key: "payment",
    label: { id: "Reminder Pembayaran", en: "Payment Reminder" },
    url: whatsappPaymentUrl,
    icon: "💰",
  },
  {
    key: "thankyou",
    label: { id: "Terima Kasih + Review", en: "Thank You + Review" },
    url: whatsappThankYouUrl,
    icon: "🙏",
  },
];
